import { Link } from 'react-router';
import {
  LogOutIcon,
  MonitorIcon,
  MoonIcon,
  SettingsIcon,
  ShieldCheckIcon,
  SunMoonIcon,
  SunIcon,
  UserIcon,
  ClipboardListIcon,
  LayoutDashboardIcon,
  CheckIcon,
  RotateCcwIcon,
} from 'lucide-react';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useSession } from '@/components/auth/user-provider';
import {
  useStaffSession,
  ROLE_LABELS,
  type ActingRole,
  type StaffRole,
} from '@/components/auth/staff-session-provider';
import { useTheme, type Theme } from '@/components/theme/theme-provider';

/** All possible switcher roles ordered from highest to lowest. */
const ALL_ROLES: ActingRole[] = ['super_admin', 'admin', 'staff', 'community'];

/**
 * Returns the roles the user can switch to: all roles at or below their
 * real staff role, plus "community". Returns empty for non-staff.
 */
function switchableRoles(realRole: StaffRole): ActingRole[] {
  const rank: Record<StaffRole, number> = {
    staff: 1,
    admin: 2,
    super_admin: 3,
  };
  return ALL_ROLES.filter((r) => rank[r as StaffRole] <= rank[realRole] || r === 'community');
}

/** Pretty label for a real staff role (no "community" — it's acting-only). */
function realRoleLabel(role: StaffRole): string {
  return ROLE_LABELS[role];
}

export function ProfileMenu() {
  const { session, signOut } = useSession();
  const { theme, setTheme } = useTheme();
  const { realStaffRole, staffRole, actingRole, isActingRole, setActingRole } =
    useStaffSession();

  if (session.status !== 'signedIn') return null;
  const { user } = session;

  // The display role: real role for staff, "Community" for non-staff.
  const displayRole = realStaffRole ? realRoleLabel(realStaffRole) : 'Community';
  const effectiveRole = staffRole ?? 'community';
  const canAccessWorkspace =
    realStaffRole === 'staff' || realStaffRole === 'admin' || realStaffRole === 'super_admin';
  const canAccessAdministration = realStaffRole === 'super_admin';
  const switcherRoles = realStaffRole ? switchableRoles(realStaffRole) : [];

  return (
    <div className="flex items-center gap-1.5">
      {isActingRole && actingRole && (
        <Badge variant="secondary" className="hidden text-xs sm:inline-flex">
          Acting as {ROLE_LABELS[actingRole]}
        </Badge>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="rounded-full" aria-label="Open profile menu">
            <Avatar>
              <AvatarFallback className="text-xs font-medium">{user.initials}</AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col gap-0.5">
              <p className="text-sm font-medium">{user.name}</p>
              <p className="text-muted-foreground truncate text-xs">{user.email}</p>
              <p className="text-muted-foreground text-xs capitalize">Role: {displayRole}</p>
              {isActingRole && actingRole && (
                <p className="text-primary text-xs font-medium">
                  Acting as {ROLE_LABELS[actingRole]}
                </p>
              )}
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />

          {/* Workspace link — visible based on the REAL staff role, even
              when acting in a downgraded role. Clicking the link while acting
              resets to the real role so RequireStaff admits the user. */}
          {canAccessWorkspace && (
            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <Link
                  to="/workspace"
                  onClick={() => {
                    if (isActingRole) setActingRole(null);
                  }}
                >
                  <LayoutDashboardIcon /> Staff workspace
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          )}

          <DropdownMenuGroup>
            {canAccessAdministration && (
              <DropdownMenuItem asChild>
                <Link
                  to="/admin"
                  onClick={() => {
                    if (isActingRole) setActingRole(null);
                  }}
                >
                  <ShieldCheckIcon /> Administration
                </Link>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem asChild>
              <Link to="/my-reports">
                <ClipboardListIcon /> My Reports
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/settings">
                <UserIcon /> Profile
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/settings">
                <SettingsIcon /> Settings
              </Link>
            </DropdownMenuItem>
          </DropdownMenuGroup>

          {/* Role switcher — only for staff users */}
          {switcherRoles.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <RotateCcwIcon /> Switch role
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuRadioGroup
                    value={effectiveRole}
                    onValueChange={(value) => {
                      const role = value as ActingRole;
                      // If the user picks their real role, reset to null (no downgrade).
                      if (realStaffRole && role === realStaffRole) {
                        setActingRole(null);
                      } else {
                        setActingRole(role);
                      }
                    }}
                  >
                    {switcherRoles.map((role) => (
                      <DropdownMenuRadioItem key={role} value={role}>
                        {ROLE_LABELS[role]}
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                  {isActingRole && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setActingRole(null)}>
                        <CheckIcon /> Return to my role
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            </>
          )}

          <DropdownMenuSeparator />
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <SunMoonIcon /> Theme
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuRadioGroup
                value={theme}
                onValueChange={(value) => setTheme(value as Theme)}
              >
                <DropdownMenuRadioItem value="light">
                  <SunIcon className="size-4" /> Light
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="dark">
                  <MoonIcon className="size-4" /> Dark
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="system">
                  <MonitorIcon className="size-4" /> System
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSeparator />
          <DropdownMenuItem variant="destructive" onClick={signOut}>
            <LogOutIcon /> Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
